/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React from "react"
import dayjs from "dayjs"
import _isEmpty from "lodash/isEmpty"
import { Avatar } from "@duxton-web/ui"
import { StarRating } from "components/common/StarRating"
import { Review } from "types/api/menuFeedback.type"
import { useTranslationsMap, useLocaleContext } from "common/utils/i18n"
import {
  DEFAULT_DATE_FORMAT,
  getDayjsLocale,
  getLocalizedDateFormat,
  TAIWAN_DATE_FORMAT,
} from "utils/i18n"
import { getFirstLetter, capitalizeFirstLetter } from "utils/misc"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { getElapsedWithLocale } from "../utils"

type DrawerReviewDetailsProps = {
  review: Review
}

const DrawerReviewDetails: React.FC<DrawerReviewDetailsProps> = ({
  review,
}) => {
  const eaterFirstLetter = getFirstLetter(review?.eaterName).toUpperCase()
  const orderedItems = review?.orderedItems
    ? review?.orderedItems?.join(", ")
    : ""

  const locale = useLocaleContext("locale")
  const dayjsLocale = getDayjsLocale(locale)
  const dateFormat = getLocalizedDateFormat(
    locale,
    DEFAULT_DATE_FORMAT,
    TAIWAN_DATE_FORMAT,
  )

  const textMap = useTranslationsMap({
    ordered: "feedback.reply.drawer.ordered",
    lessth1hr: "feedback.reply.drawer.lessth1hr",
    exactly1hr: "feedback.reply.drawer.exactly1hr",
    elapsedhours: "feedback.reply.drawer.elapsedhours",
    statusPublic: "feedback.statuses.public",
    statusReported: "feedback.statuses.reported",
    statusPrivate: "feedback.statuses.private",
    statusRemoved: "feedback.statuses.removed",
  })

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      public: textMap.statusPublic,
      reported: textMap.statusReported,
      private: textMap.statusPrivate,
      removed: textMap.statusRemoved,
    }
    return (
      statusMap[status.toLowerCase()] ||
      capitalizeFirstLetter(status.toLowerCase())
    )
  }

  TroyWebTracker.sendEvent({
    category: EventCategory.feedbackReportDetails,
    action: EventAction.reportStatus,
    labels: {
      REVIEW_ID: review?.reviewID,
      REVIEW_RATING: review?.rating,
      REPORT_STATUS: review?.status,
      DEPRECATE_REVIEW_ORDER_ID: true,
    },
  })

  return (
    <div
      css={css({
        margin: "35px 25px",
      })}
    >
      <div
        css={css({
          display: "flex",
          marginBottom: "20px",
        })}
      >
        <Avatar
          size={40}
          style={{
            backgroundColor: "#136fd8",
            color: "#ffffff",
            fontWeight: 500,
            marginRight: 10,
          }}
        >
          {eaterFirstLetter}
        </Avatar>
        <div>
          <span style={{ fontWeight: 500, fontSize: 16 }}>
            {review?.eaterName}
          </span>
          <div
            css={css({
              fontSize: "12px",
              color: "#707070",
            })}
          >
            <span style={{ fontWeight: 500 }}>{`${review?.merchantName}`}</span>
            <span>{` • ${getStatusLabel(review?.status)}`}</span>
            <span>{` • ${getElapsedWithLocale(
              dayjs(review?.contentLastModifiedAt),
              textMap,
              { dayjsLocale, dateFormat },
            )}`}</span>
          </div>
        </div>
      </div>

      <div
        css={css({
          marginBottom: "10px",
        })}
      >
        <StarRating rating={review?.rating} />
        <span
          css={css({
            fontSize: "12px",
            color: "#707070",
          })}
        >
          {` • ${review?.reviewID}`}
        </span>
      </div>

      <div
        css={css({
          marginBottom: "10px",
        })}
      >
        <span
          css={css({
            whiteSpace: "pre-line",
          })}
        >
          {review?.description}
        </span>
      </div>

      <div>
        <span
          css={css({
            fontSize: "12px",
            color: "#707070",
          })}
        >
          {!_isEmpty(orderedItems) && `${textMap.ordered} ${orderedItems}`}
        </span>
      </div>
    </div>
  )
}

export default React.memo(DrawerReviewDetails)
